import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { uploadArtifact } from "@/services/api/artifacts";
import { cn } from "@/utils/cn";

const PLACEHOLDER_POOL = [
  "Walk me through what's required, then tell me what's missing in our evidence.",
  "Cross-reference NIS2 with our DORA programme — show me the diff.",
  "If a CVE drops at 16:00, what reporting clock starts ticking?",
  "What does 'state of the art' actually mean in 2026?",
  "Map this control to every framework we cover.",
];

export interface PendingAttachment {
  id: string;
  filename: string;
  size: number;
  mediaType: string;
}

/** Docs already indexed into the current session's corpus (B-3b chip).
 * Visually similar to PendingAttachment chips but carry a different
 * remove action: deleting these hits the backend's
 * `DELETE /chat/sessions/{id}/attachments/{artifact_id}` endpoint. */
export interface SessionScopeAttachment {
  artifact_id: string;
  filename: string | null;
  chunk_count: number;
}

interface Props {
  onSubmit: (query: string, attachments: PendingAttachment[]) => void;
  disabled?: boolean;
  isStreaming?: boolean;
  suggestions?: string[];
  /** Attachments persisted to the current session's corpus.
   * Rendered as a separate chip row above the pending one. */
  sessionAttachments?: SessionScopeAttachment[];
  /** Called when the user clicks × on a session-scope chip. The parent
   * (Chat page) fires the DELETE request and refetches. */
  onRemoveSessionAttachment?: (artifactId: string) => void;
}

function pickPlaceholder(): string {
  return PLACEHOLDER_POOL[Math.floor(Math.random() * PLACEHOLDER_POOL.length)];
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Paste anything longer than this and it becomes a chip instead of filling the
 * box — roughly a page and a half, so ordinary paragraphs and code snippets
 * still paste inline. Same idea as ChatGPT and Claude.
 */
const PASTE_CHIP_THRESHOLD = 2_000;

/** Grow with the content, then scroll inside rather than swallow the screen. */
const TEXTAREA_MAX_HEIGHT = 200;

interface PastedBlock {
  id: string;
  text: string;
}

function formatCharCount(n: number): string {
  return n < 1000 ? `${n} chars` : `${(n / 1000).toFixed(1)}k chars`;
}

export function ChatComposer({
  onSubmit,
  disabled = false,
  isStreaming = false,
  suggestions = [],
  sessionAttachments = [],
  onRemoveSessionAttachment,
}: Props) {
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [pasted, setPasted] = useState<PastedBlock[]>([]);
  const [uploading, setUploading] = useState(false);
  const placeholder = useMemo(pickPlaceholder, []);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Grow the box to fit its content, up to a cap. Starting at one row means a
  // short message sits centred in its own padding instead of clinging to the
  // top of a fixed two-row box.
  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
  };

  useEffect(autoGrow, [value]);

  // Rotate one suggestion chip every 5 seconds.
  const [visibleSuggestions, setVisibleSuggestions] = useState<string[]>(() =>
    suggestions.slice(0, 3),
  );
  useEffect(() => {
    setVisibleSuggestions(suggestions.slice(0, 3));
  }, [suggestions]);
  useEffect(() => {
    if (suggestions.length <= 3) return;
    const id = window.setInterval(() => {
      setVisibleSuggestions((current) => {
        const remaining = suggestions.filter((s) => !current.includes(s));
        if (!remaining.length) return current;
        const swapIdx = Math.floor(Math.random() * current.length);
        const incoming = remaining[Math.floor(Math.random() * remaining.length)];
        const next = [...current];
        next[swapIdx] = incoming;
        return next;
      });
    }, 5_000);
    return () => window.clearInterval(id);
  }, [suggestions]);

  const submit = () => {
    const typed = value.trim();
    // A chip is a presentation choice, not a truncation — the pasted text is
    // still sent, appended under a delimiter so the agent can tell it apart
    // from the question.
    const q = [typed, ...pasted.map((p) => `--- pasted ---\n${p.text}`)]
      .filter(Boolean)
      .join("\n\n");
    if (!q || disabled) return;
    onSubmit(q, attachments);
    setValue("");
    setAttachments([]);
    setPasted([]);
  };

  /**
   * A large paste becomes a chip rather than burying the composer. Small
   * pastes fall through to the browser's default handling.
   */
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData("text/plain");
    if (text.length <= PASTE_CHIP_THRESHOLD) return;
    e.preventDefault();
    setPasted((prev) => [
      ...prev,
      { id: `paste-${prev.length}-${text.length}`, text },
    ]);
  };

  const removePasted = (id: string) => {
    setPasted((prev) => prev.filter((p) => p.id !== id));
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onFilesSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const result = await uploadArtifact(file);
        setAttachments((prev) => [
          ...prev,
          {
            id: result.id,
            filename: result.filename,
            size: file.size,
            mediaType: result.media_type,
          },
        ]);
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <div className="space-y-3">
      {sessionAttachments.length ? (
        <div className="space-y-1">
          <span className="block text-[0.65rem] uppercase tracking-wider text-warmgray-400">
            In this session
          </span>
          <ul className="flex flex-wrap gap-2">
            {sessionAttachments.map((a) => (
              <li
                key={a.artifact_id}
                className="inline-flex items-center gap-2 rounded-full border border-oxblood-300 bg-oxblood-50 px-3 py-1 text-xs text-espresso-700"
                title={`${a.chunk_count} chunks indexed — searchable by the agent`}
              >
                <span className="font-mono">{a.filename || a.artifact_id}</span>
                <span className="text-warmgray-500">{a.chunk_count} chunks</span>
                {onRemoveSessionAttachment ? (
                  <button
                    type="button"
                    onClick={() => onRemoveSessionAttachment(a.artifact_id)}
                    className="text-warmgray-500 hover:text-oxblood-500"
                    aria-label={`Remove ${a.filename || a.artifact_id} from session`}
                  >
                    ✕
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {pasted.length ? (
        <ul className="flex flex-wrap gap-2">
          {pasted.map((p) => (
            <li
              key={p.id}
              className="inline-flex items-center gap-2 rounded-full border border-ruleline bg-parchment-50 px-3 py-1 text-xs text-warmgray-500"
              title="Sent with your question"
            >
              <span className="font-mono">Pasted text</span>
              <span className="text-warmgray-400">{formatCharCount(p.text.length)}</span>
              <button
                type="button"
                onClick={() => removePasted(p.id)}
                className="text-warmgray-400 transition-colors duration-200 ease-atelier hover:text-oxblood-500"
                aria-label="Remove pasted text"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {attachments.length ? (
        <ul className="flex flex-wrap gap-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="inline-flex items-center gap-2 rounded-full border border-ruleline bg-parchment-50 px-3 py-1 text-xs text-warmgray-500"
            >
              <span className="font-mono">{a.filename}</span>
              <span className="text-warmgray-400">{formatBytes(a.size)}</span>
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                className="text-warmgray-400 hover:text-oxblood-500"
                aria-label={`Remove ${a.filename}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex items-end gap-3">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={onFilesSelected}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || disabled}
          className={cn(
            "flex h-[48px] w-[44px] shrink-0 items-center justify-center rounded-field border border-ruleline bg-parchment-50 text-warmgray-500",
            "transition-colors duration-200 ease-atelier",
            "hover:border-oxblood-300 hover:text-oxblood-500",
            "disabled:cursor-not-allowed disabled:opacity-40",
          )}
          aria-label="Attach files"
          title="Attach files"
        >
          {uploading ? (
            <span className="font-mono text-xs">…</span>
          ) : (
            // Paperclip glyph
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 1 1-8.49-8.49l8.57-8.57a4 4 0 1 1 5.66 5.66l-8.59 8.57a2 2 0 1 1-2.83-2.83l8.49-8.48" />
            </svg>
          )}
        </button>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKey}
          onPaste={onPaste}
          placeholder={placeholder}
          // One row, grown by autoGrow(). A fixed two-row box left a single
          // line stranded at the top; at one row the padding centres it.
          rows={1}
          className="composer flex-1 resize-none overflow-hidden"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || (!value.trim() && pasted.length === 0)}
          aria-label="Ask"
          className={cn(
            "group relative flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full border border-oxblood-500 bg-oxblood-500 text-parchment-50",
            "shadow-send transition duration-200 ease-atelier",
            "hover:-translate-y-px hover:bg-oxblood-600 hover:shadow-lift active:translate-y-0 active:scale-[0.97]",
            "disabled:cursor-not-allowed disabled:border-warmgray-200 disabled:bg-warmgray-200 disabled:text-warmgray-400 disabled:shadow-none disabled:hover:translate-y-0",
            isStreaming && "animate-ery-shimmer",
          )}
        >
          <span className="font-serif text-xl leading-none transition-transform group-hover:translate-x-0.5">→</span>
        </button>
      </div>

      {visibleSuggestions.length ? (
        <div className="flex flex-wrap gap-2">
          {visibleSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setValue(s)}
              className="action-pill animate-ery-rise h-auto py-1.5 text-left"
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
